'use client';

import { BuilderComponent, useIsPreviewing } from '@builder.io/react';
import { BuilderContent } from '@builder.io/sdk';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../builder-registry';
import { modifyLinksWithUtmCampaignRef } from '@/domain/campaign';
import { isPreview } from '@/utils/isPreview';

type BuilderPageProps = ComponentProps<typeof BuilderComponent>;

function getPreviewModelFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('builder.preview');
}

function trackPageView(content: BuilderContent | null) {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];

  const alreadyTracked = window.dataLayer.some((event: { event_name?: string }) => event.event_name === 'page_view');
  if (alreadyTracked) return;

  window.dataLayer.push({
    event: 'amplitude_event',
    event_name: 'page_view',
    event_properties: {
      contentId: content?.id,
      contentName: content?.name,
      testVariationId: content?.testVariationId,
      testVariationName: content?.testVariationName,
    },
  });
}

export function RenderBuilderContent({ content, model, data }: BuilderPageProps) {
  const isPreviewing = useIsPreviewing();
  const [previewModel, setPreviewModel] = useState<string | null>(null);

  const ssrContentRef = useRef(content);
  const [useSsrFallback, setUseSsrFallback] = useState(false);

  useEffect(() => {
    const urlModel = getPreviewModelFromUrl();
    if (urlModel) setPreviewModel(urlModel);
    modifyLinksWithUtmCampaignRef();
  }, []);

  const effectiveModel = isPreviewing && previewModel ? previewModel : model;

  const handleContentLoaded = useCallback((_: unknown, loadedContent: BuilderContent) => {
    if (!loadedContent && ssrContentRef.current) {
      setUseSsrFallback(true);
    }
    trackPageView(loadedContent);
  }, []);

  const builderOptions = useMemo(
    () => ({
      enrich: true,
      includeUnpublished: isPreview() || isPreviewing,
    }),
    [isPreviewing]
  );

  // SSR Fallback: render without model prop to prevent refetching
  if (useSsrFallback && ssrContentRef.current) {
    return (
      <BuilderComponent
        key="ssr-fallback"
        apiKey={process.env.NEXT_PUBLIC_BUILDER_API_KEY}
        content={ssrContentRef.current}
        data={data}
      />
    );
  }

  if (content || isPreviewing) {
    return (
      <BuilderComponent
        key={content?.id ?? 'preview'}
        apiKey={process.env.NEXT_PUBLIC_BUILDER_API_KEY}
        content={content}
        contentLoaded={handleContentLoaded}
        model={effectiveModel}
        data={data}
        options={builderOptions}
      />
    );
  }

  return null;
}
